// frontend/src/components/SEO/SEOEditor.jsx
import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Globe, Eye, TrendingUp, Hash, Zap } from 'lucide-react';

export const SEOEditor = ({ 
  initialData, 
  onSave, 
  onCancel, 
  content = '', 
  title = 'تنظیمات سئو',
  keywordsSuggestion = [] 
}) => {
  const [seoData, setSeoData] = useState({
    meta_title: initialData?.meta_title || '',
    meta_description: initialData?.meta_description || '',
    meta_keywords: initialData?.meta_keywords || '',
    focus_keyword: initialData?.focus_keyword || '',
  });
  
  const [analysis, setAnalysis] = useState({
    titleLength: { status: 'pending', message: '' },
    descriptionLength: { status: 'pending', message: '' },
    keywordInTitle: { status: 'pending', message: '' },
    keywordInDescription: { status: 'pending', message: '' },
    keywordInContent: { status: 'pending', message: '', count: 0 },
    keywordDensity: { status: 'pending', message: '', density: 0 },
  });

  useEffect(() => {
    analyzeSEO();
  }, [seoData.focus_keyword, content, seoData.meta_title, seoData.meta_description]);

  const analyzeSEO = () => {
    const newAnalysis = { ...analysis };
    const keyword = seoData.focus_keyword?.toLowerCase().trim();
    
    // آنالیز طول تایتل
    const titleLength = seoData.meta_title.length;
    if (titleLength === 0) {
      newAnalysis.titleLength = { status: 'error', message: 'متا تایتل را وارد کنید' };
    } else if (titleLength < 30) {
      newAnalysis.titleLength = { status: 'warning', message: `تایتل بسیار کوتاه است (${titleLength}/150). حداقل 30 کاراکتر توصیه می‌شود.` };
    } else if (titleLength > 150) {
      newAnalysis.titleLength = { status: 'error', message: `تایتل بسیار بلند است (${titleLength}/150). حداکثر 150 کاراکتر مجاز است.` };
    } else {
      newAnalysis.titleLength = { status: 'success', message: `عالی است! (${titleLength}/150 کاراکتر)` };
    }
    
    // آنالیز طول دیسکریپشن
    const descLength = seoData.meta_description.length;
    if (descLength === 0) {
      newAnalysis.descriptionLength = { status: 'error', message: 'متا دیسکریپشن را وارد کنید' };
    } else if (descLength < 120) {
      newAnalysis.descriptionLength = { status: 'warning', message: `توضیحات کوتاه است (${descLength}/300). حداقل 120 کاراکتر توصیه می‌شود.` };
    } else if (descLength > 300) {
      newAnalysis.descriptionLength = { status: 'error', message: `توضیحات بلند است (${descLength}/300). حداکثر 300 کاراکتر مجاز است.` };
    } else {
      newAnalysis.descriptionLength = { status: 'success', message: `عالی است! (${descLength}/300 کاراکتر)` };
    }
    
    // آنالیز کلمه کلیدی در تایتل
    if (keyword && seoData.meta_title.toLowerCase().includes(keyword)) {
      newAnalysis.keywordInTitle = { status: 'success', message: '✓ کلمه کلیدی در تایتل وجود دارد' };
    } else if (keyword) {
      newAnalysis.keywordInTitle = { status: 'error', message: '✗ کلمه کلیدی در تایتل وجود ندارد' };
    } else {
      newAnalysis.keywordInTitle = { status: 'info', message: 'کلمه کلیدی اصلی را وارد کنید' };
    }
    
    // آنالیز کلمه کلیدی در دیسکریپشن
    if (keyword && seoData.meta_description.toLowerCase().includes(keyword)) {
      newAnalysis.keywordInDescription = { status: 'success', message: '✓ کلمه کلیدی در توضیحات وجود دارد' };
    } else if (keyword) {
      newAnalysis.keywordInDescription = { status: 'error', message: '✗ کلمه کلیدی در توضیحات وجود ندارد' };
    } else {
      newAnalysis.keywordInDescription = { status: 'info', message: 'کلمه کلیدی اصلی را وارد کنید' };
    }
    
    // آنالیز کلمه کلیدی در محتوا
    if (keyword && content) {
      const contentLower = content.toLowerCase();
      const count = (contentLower.match(new RegExp(keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')) || []).length;
      const totalWords = content.split(/\s+/).length;
      const density = totalWords > 0 ? (count / totalWords) * 100 : 0;
      
      if (count === 0) {
        newAnalysis.keywordInContent = { status: 'error', message: '✗ کلمه کلیدی در محتوا وجود ندارد', count: 0 };
      } else if (count < 3) {
        newAnalysis.keywordInContent = { status: 'warning', message: `⚠ کلمه کلیدی فقط ${count} بار تکرار شده. حداقل 3 بار توصیه می‌شود.`, count };
      } else if (count > 10) {
        newAnalysis.keywordInContent = { status: 'warning', message: `⚠ کلمه کلیدی ${count} بار تکرار شده (زیاد). ممکن است به عنوان spam شناخته شود.`, count };
      } else {
        newAnalysis.keywordInContent = { status: 'success', message: `✓ کلمه کلیدی ${count} بار تکرار شده (عالی)`, count };
      }
      
      newAnalysis.keywordDensity = { 
        status: density > 5 ? 'warning' : density >= 1 ? 'success' : 'info',
        message: density > 5 ? 'چگالی کلمات کلیدی بالاست (بیش از 5%)' : `چگالی کلمات کلیدی: ${density.toFixed(1)}% (مناسب: 1-3%)`,
        density
      };
    }
    
    setAnalysis(newAnalysis);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'success': return 'bg-green-50 border-green-200 text-green-700';
      case 'error': return 'bg-red-50 border-red-200 text-red-700';
      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      default: return 'bg-gray-50 border-gray-200 text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Globe className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            انصراف
          </button>
          <button onClick={() => onSave(seoData)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition">
            ذخیره تنظیمات سئو
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* فرم سئو */}
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              کلمه کلیدی اصلی (Focus Keyword)
            </label>
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={seoData.focus_keyword}
                onChange={(e) => setSeoData({ ...seoData, focus_keyword: e.target.value })}
                className="flex-1 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="مثال: پنل خورشیدی 10 کیلووات"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">کلمه کلیدی اصلی که می‌خواهید برای این صفحه رتبه بگیرید</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              متا تایتل (Meta Title)
            </label>
            <input
              type="text"
              value={seoData.meta_title}
              onChange={(e) => setSeoData({ ...seoData, meta_title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              maxLength="150"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">{seoData.meta_title.length}/150 کاراکتر</p>
              <p className="text-xs text-gray-400">حداقل 30 کاراکتر توصیه می‌شود</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              متا دیسکریپشن (Meta Description)
            </label>
            <textarea
              value={seoData.meta_description}
              onChange={(e) => setSeoData({ ...seoData, meta_description: e.target.value })}
              rows="3"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              maxLength="300"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">{seoData.meta_description.length}/300 کاراکتر</p>
              <p className="text-xs text-gray-400">حداقل 120 کاراکتر توصیه می‌شود</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              کلمات کلیدی (با کاما جدا کنید)
            </label>
            <input
              type="text"
              value={seoData.meta_keywords}
              onChange={(e) => setSeoData({ ...seoData, meta_keywords: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="پنل خورشیدی، اینورتر خورشیدی، انرژی خورشیدی، ..."
            />
            <p className="text-xs text-gray-500 mt-1">5 تا 10 کلمه کلیدی مرتبط را با کاما جدا کنید</p>
          </div>
        </div>

        {/* آنالیز و پیش‌نمایش */}
        <div className="space-y-5">
          {/* پیش‌نمایش گوگل */}
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">پیش‌نمایش در نتایج جستجوی گوگل</span>
            </div>
            <div className="space-y-1">
              <div className="text-blue-800 text-lg hover:underline cursor-pointer">
                {seoData.meta_title || 'عنوان صفحه'}
              </div>
              <div className="text-green-700 text-sm text-wrap break-all">
                {window.location.href}
              </div>
              <div className="text-gray-600 text-sm line-clamp-2">
                {seoData.meta_description || 'توضیحات صفحه در نتایج جستجو نمایش داده می‌شود...'}
              </div>
            </div>
          </div>

          {/* آنالیز سئو */}
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">آنالیز سئو</span>
            </div>
            <div className="space-y-3">
              <div className={`p-2 rounded ${getStatusClass(analysis.titleLength.status)} text-sm flex items-center gap-2`}>
                {getStatusIcon(analysis.titleLength.status)}
                <span>{analysis.titleLength.message}</span>
              </div>
              <div className={`p-2 rounded ${getStatusClass(analysis.descriptionLength.status)} text-sm flex items-center gap-2`}>
                {getStatusIcon(analysis.descriptionLength.status)}
                <span>{analysis.descriptionLength.message}</span>
              </div>
              <div className={`p-2 rounded ${getStatusClass(analysis.keywordInTitle.status)} text-sm flex items-center gap-2`}>
                {getStatusIcon(analysis.keywordInTitle.status)}
                <span>{analysis.keywordInTitle.message}</span>
              </div>
              <div className={`p-2 rounded ${getStatusClass(analysis.keywordInDescription.status)} text-sm flex items-center gap-2`}>
                {getStatusIcon(analysis.keywordInDescription.status)}
                <span>{analysis.keywordInDescription.message}</span>
              </div>
              <div className={`p-2 rounded ${getStatusClass(analysis.keywordInContent.status)} text-sm flex items-center gap-2`}>
                {getStatusIcon(analysis.keywordInContent.status)}
                <span>{analysis.keywordInContent.message}</span>
              </div>
              {analysis.keywordDensity.status !== 'pending' && (
                <div className={`p-2 rounded ${getStatusClass(analysis.keywordDensity.status)} text-sm flex items-center gap-2`}>
                  {getStatusIcon(analysis.keywordDensity.status)}
                  <span>{analysis.keywordDensity.message}</span>
                </div>
              )}
            </div>
          </div>

          {/* پیشنهادات */}
          {keywordsSuggestion && keywordsSuggestion.length > 0 && !seoData.meta_keywords && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-700">پیشنهاد کلمات کلیدی</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {keywordsSuggestion.slice(0, 5).map((kw, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const current = seoData.meta_keywords;
                      const newKeywords = current ? `${current}, ${kw}` : kw;
                      setSeoData({ ...seoData, meta_keywords: newKeywords });
                    }}
                    className="text-sm bg-white text-blue-600 px-2 py-1 rounded border border-blue-300 hover:bg-blue-100 transition"
                  >
                    + {kw}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};